import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { FiPlus, FiEdit2, FiPackage, FiX, FiTrash2, FiToggleLeft, FiToggleRight, FiChevronDown, FiSearch } from "react-icons/fi";
import Swal from 'sweetalert2';
import Header from "../component/Header";
import { API_BASE_URL } from "../utils/apiConfig";

function FlavorsPage() {
    const [summary, setSummary] = useState({
        totalFlavors: 0,
        lowStockAlerts: 0,
        avgDailyUsage: "0%"
    });
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [stocks, setStocks] = useState([]);
    const [basesStock, setBasesStock] = useState([]);
    const [flavors, setFlavors] = useState([]);
    const [availableBases, setAvailableBases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isAddBaseModalOpen, setIsAddBaseModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isEditBaseModalOpen, setIsEditBaseModalOpen] = useState(false);
    const [isEditFlavorModalOpen, setIsEditFlavorModalOpen] = useState(false);
    const [isNewFlavorModalOpen, setIsNewFlavorModalOpen] = useState(false);
    const [isNewBaseModalOpen, setIsNewBaseModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [editBaseTarget, setEditBaseTarget] = useState(null);
    const [editFlavorTarget, setEditFlavorTarget] = useState(null);
    const [newInventory, setNewInventory] = useState({ flavor_id: "", stock: 0, price: "", loadCell_id_FK: "" });
    const [newBaseInventory, setNewBaseInventory] = useState({ base_id: "", stock: 0, price: "", loadCell_id_FK: "" });
    const [newFlavor, setNewFlavor] = useState({ name: "", device_id: "", Colories: "", Protein: "", Carbs: "", Fat: "", Fibre: "", Sugar: "", image: null, banner: null });
    const [newBase, setNewBase] = useState({ name: "", price: "", image: null });
    const [loadCells, setLoadCells] = useState([]);
    const userRole = localStorage.getItem("role");

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const dropdownRef = useRef(null);
    const [isEditFlavorDropdownOpen, setIsEditFlavorDropdownOpen] = useState(false);
    const [editFlavorSearchQuery, setEditFlavorSearchQuery] = useState("");
    const editFlavorDropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (editFlavorDropdownRef.current && !editFlavorDropdownRef.current.contains(event.target)) {
                setIsEditFlavorDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchDeviceStock = useCallback(async (deviceId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/machine-flavor-stock/device/${deviceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // The API now returns { status: true, flavors: [...], bases: [...] }
            if (response.data && typeof response.data === 'object' && response.data.flavors) {
                setStocks(response.data.flavors);
                if (response.data.bases) {
                    setBasesStock(response.data.bases);
                }
            } else {
                // Fallback for older API versions or direct array responses
                setStocks(Array.isArray(response.data) ? response.data : []);
            }
        } catch (err) {
            console.error("Failed to fetch device stock", err);
            setStocks([]); // Reset on error to prevent .map crashes
        }
    }, []);

    const fetchDeviceBaseStock = useCallback(async (deviceId) => {
        try {
            const token = localStorage.getItem("token");
            const response = await axios.get(`${API_BASE_URL}/api/machine-base-stock/device/${deviceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBasesStock(response.data);
        } catch (err) {
            console.error("Failed to fetch device base stock", err);
        }
    }, []);

    const fetchInitialData = useCallback(async (initialDeviceId) => {
        try {
            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };

            // Fetch global summary
            const summaryRes = await axios.get(`${API_BASE_URL}/api/flavors/inventory/summary`, config);
            setSummary(summaryRes.data);

            // Fetch devices
            const devicesRes = await axios.get(`${API_BASE_URL}/api/devices`, config);
            setDevices(devicesRes.data);

            let effectiveId = initialDeviceId;
            if (devicesRes.data.length > 0) {
                effectiveId = initialDeviceId || devicesRes.data[0].device_id_PK;
                setSelectedDeviceId(effectiveId);
                fetchDeviceStock(effectiveId);
                fetchDeviceBaseStock(effectiveId);
            }

            // Fetch all available flavors filtered by device ID
            let flavorsUrl = `${API_BASE_URL}/api/flavors`;
            if (effectiveId) {
                flavorsUrl += `?device_id=${effectiveId}`;
            }
            const flavorsRes = await axios.get(flavorsUrl, config);
            setFlavors(flavorsRes.data);

            // Fetch all available bases for the "Add Base" dropdown
            const basesRes = await axios.get(`${API_BASE_URL}/api/base`, config);
            setAvailableBases(basesRes.data);

            // Fetch all available load cells
            const loadCellsRes = await axios.get(`${API_BASE_URL}/api/loadcells/get-loadcells`, config);
            if (loadCellsRes.data && loadCellsRes.data.status) {
                setLoadCells(loadCellsRes.data.data);
            }

        } catch (err) {
            console.error("Failed to load initial inventory data", err);
        } finally {
            setLoading(false);
        }
    }, [fetchDeviceStock, fetchDeviceBaseStock]);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const deviceParam = queryParams.get("deviceId");
        fetchInitialData(deviceParam);
    }, [fetchInitialData]);

    useEffect(() => {
        if (stocks.length > 0) {
            console.log("Current Machine Stocks Data:", stocks);
        }
    }, [stocks]);


    const fetchFlavorsByDevice = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const flavorsRes = await axios.get(`${API_BASE_URL}/api/flavors?device_id=${id}`, config);
            setFlavors(flavorsRes.data);
        } catch (err) {
            console.error("Failed to fetch flavors by device", err);
        }
    };

    const handleDeviceChange = (e) => {
        const id = e.target.value;
        setSelectedDeviceId(id);
        fetchDeviceStock(id);
        fetchDeviceBaseStock(id);
        fetchFlavorsByDevice(id);
    };

    const handleAddInventory = async () => {
        if (!newInventory.flavor_id) return Swal.fire({ icon: 'error', title: 'Error', text: 'Select a flavor', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        if (!newInventory.loadCell_id_FK) return Swal.fire({ icon: 'error', title: 'Error', text: 'Select a load cell', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/add-stock`, {
                device_id: String(selectedDeviceId),
                flavor_id: String(newInventory.flavor_id),
                stock: String(newInventory.stock),
                price: String(newInventory.price),
                loadCell_id_FK: String(newInventory.loadCell_id_FK)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIsAddModalOpen(false);
            setNewInventory({ flavor_id: "", stock: 0, price: "", loadCell_id_FK: "" });
            fetchDeviceStock(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Added!', text: 'Inventory has been added successfully.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to add inventory", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleAddBaseInventory = async () => {
        if (!newBaseInventory.base_id) return Swal.fire({ icon: 'error', title: 'Error', text: 'Select a base', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-base-stock/add`, {
                device_id: String(selectedDeviceId),
                base_id: String(newBaseInventory.base_id),
                stock: String(newBaseInventory.stock),
                price: String(newBaseInventory.price),
                loadCell_id_FK: String(newBaseInventory.loadCell_id_FK)
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            setIsAddBaseModalOpen(false);
            setNewBaseInventory({ base_id: "", stock: 0, price: "", loadCell_id_FK: "" });
            fetchDeviceBaseStock(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Added!', text: 'Base inventory has been added successfully.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to add base inventory", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleEditBaseStock = async () => {
        if (!editBaseTarget) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-base-stock/update`, {
                device_id: String(selectedDeviceId),
                base_id: String(editBaseTarget.base_id_FK),
                stock: String(editBaseTarget.stock),
                price: String(editBaseTarget.price),
                loadCell_id_FK: String(editBaseTarget.loadCell_id_FK)
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            setIsEditBaseModalOpen(false);
            fetchDeviceBaseStock(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Base stock has been updated.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update base stock and price", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleEditStock = async () => {
        if (!editTarget) return;
        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/update`, {
                device_id: String(selectedDeviceId),
                flavor_id: String(editTarget.flavor_id_FK),
                stock: String(editTarget.stock),
                price: String(editTarget.price),
                loadCell_id_FK: String(editTarget.loadCell_id_FK)
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setIsEditModalOpen(false);
            fetchDeviceStock(selectedDeviceId);
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Updated!', text: 'Stock has been updated successfully.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update stock and price", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleEditFlavor = async () => {
        if (!editFlavorTarget) return;
        const formData = new FormData();
        formData.append("name", editFlavorTarget.name);
        formData.append("price", editFlavorTarget.price || "");
        formData.append("Colories", editFlavorTarget.Colories || "0");
        formData.append("Protein", editFlavorTarget.Protein || "0");
        formData.append("Carbs", editFlavorTarget.Carbs || "0");
        formData.append("Fat", editFlavorTarget.Fat || "0");
        formData.append("Fibre", editFlavorTarget.Fibre || "0");
        formData.append("Sugar", editFlavorTarget.Sugar || "0");
        if (editFlavorTarget.imageFile) {
            formData.append("image", editFlavorTarget.imageFile);
        }
        if (editFlavorTarget.bannerFile) {
            formData.append("banner", editFlavorTarget.bannerFile);
        }

        try {
            const token = localStorage.getItem("token");
            await axios.put(`${API_BASE_URL}/api/flavors/${editFlavorTarget.flavors_id_PK}`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setIsEditFlavorModalOpen(false);
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Saved!', text: 'Flavor details have been updated.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to update flavor", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleToggleActivity = async (item) => {
        const isCurrentlyActive = item.status == 0;
        const actionLabel = isCurrentlyActive ? 'Deactivate' : 'Activate';
        const newStatusLabel = isCurrentlyActive ? 'Inactive' : 'Active';
        const confirmColor = isCurrentlyActive ? '#ef4444' : '#22c55e';

        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `${actionLabel} "${item.flavor_name}"? It will be marked as ${newStatusLabel}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: confirmColor,
            cancelButtonColor: '#6b7280',
            confirmButtonText: `Yes, ${actionLabel}!`,
            cancelButtonText: 'Cancel',
            background: '#1b1d1a',
            color: '#fff'
        });

        if (!result.isConfirmed) return;

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/activity`, {
                device_id_FK: String(selectedDeviceId),
                flavor_id_FK: String(item.flavor_id_FK),
                loadCell_id_FK: String(item.loadCell_id_FK)
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            fetchDeviceStock(selectedDeviceId);
            Swal.fire({
                icon: 'success',
                title: `${newStatusLabel}!`,
                text: `${item.flavor_name} has been set to ${newStatusLabel}.`,
                background: '#1b1d1a',
                color: '#fff',
                confirmButtonColor: '#22c55e',
                timer: 1800,
                showConfirmButton: false
            });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || "Failed to toggle activity", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleDeleteFlavorStock = async (item) => {
        Swal.fire({
            title: "Are you sure?",
            text: `You want to delete ${item.flavor_name} from this machine?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete it!",
            background: '#1b1d1a',
            color: '#fff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("token");
                    await axios.post(`${API_BASE_URL}/api/machine-flavor-stock/delete`, {
                        device_id: String(selectedDeviceId),
                        flavor_id: String(item.flavor_id_FK)
                    }, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    fetchDeviceStock(selectedDeviceId);
                    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Flavor stock has been removed.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to delete flavor stock", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
                }
            }
        });
    };

    const handleDeleteFlavor = async (flavorId) => {
        Swal.fire({
            title: "Are you sure?",
            text: `You want to delete this flavor globally?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#22c55e",
            cancelButtonColor: "#ef4444",
            confirmButtonText: "Yes, delete it!",
            background: '#1b1d1a',
            color: '#fff'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const token = localStorage.getItem("token");
                    await axios.delete(`${API_BASE_URL}/api/flavors/${flavorId}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    fetchInitialData(selectedDeviceId);
                    Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Flavor has been deleted globally.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
                } catch (err) {
                    Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.message || "Failed to delete flavor", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
                }
            }
        });
    };

    const handleAddNewFlavor = async () => {
        if (!newFlavor.name) {
            return Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill the flavour name.',
                background: '#1b1d1a',
                color: '#fff',
                confirmButtonColor: '#22c55e'
            });
        }

        // Updated Nginx limit is 50MB, but let's restrict to 5MB for reasonable image sizes
        const MAX_SIZE = 5 * 1024 * 1024;
        if (newFlavor.image && newFlavor.image.size > MAX_SIZE) {
            return Swal.fire({ icon: 'warning', title: 'File Too Large', text: 'Flavour image must be under 5MB.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
        if (newFlavor.banner && newFlavor.banner.size > MAX_SIZE) {
            return Swal.fire({ icon: 'warning', title: 'File Too Large', text: 'Banner image must be under 5MB.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }

        const formData = new FormData();
        formData.append("name", newFlavor.name);
        formData.append("Colories", newFlavor.Colories || "0");
        formData.append("Protein", newFlavor.Protein || "0");
        formData.append("Carbs", newFlavor.Carbs || "0");
        formData.append("Fat", newFlavor.Fat || "0");
        formData.append("Fibre", newFlavor.Fibre || "0");
        formData.append("Sugar", newFlavor.Sugar || "0");
        if (selectedDeviceId) formData.append("device_id", selectedDeviceId);
        if (newFlavor.image) formData.append("image", newFlavor.image);
        if (newFlavor.banner) formData.append("banner", newFlavor.banner);

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/flavors`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setIsNewFlavorModalOpen(false);
            setNewFlavor({
                name: "",
                device_id: "",
                Colories: "",
                Protein: "",
                Carbs: "",
                Fat: "",
                Fibre: "",
                Sugar: "",
                image: null,
                banner: null
            });
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Success!', text: 'New flavor added to catalog.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.message || err.message || "Failed to add new flavor";
            Swal.fire({ icon: 'error', title: 'Error', text: errMsg, background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const handleAddNewBase = async () => {
        if (!newBase.name || !newBase.price || !newBase.image) {
            return Swal.fire({
                icon: 'warning',
                title: 'Missing Fields',
                text: 'Please fill name, price, and upload an image.',
                background: '#1b1d1a',
                color: '#fff',
                confirmButtonColor: '#22c55e'
            });
        }
        const formData = new FormData();
        formData.append("name", newBase.name);
        formData.append("price", newBase.price);
        formData.append("image", newBase.image);

        try {
            const token = localStorage.getItem("token");
            await axios.post(`${API_BASE_URL}/api/base`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setIsNewBaseModalOpen(false);
            setNewBase({
                name: "",
                price: "",
                image: null
            });
            fetchInitialData(selectedDeviceId);
            Swal.fire({ icon: 'success', title: 'Success!', text: 'New base added to catalog.', background: '#1b1d1a', color: '#fff', confirmButtonColor: '#22c55e' });
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: "Failed to add new base", background: '#1b1d1a', color: '#fff', confirmButtonColor: '#ef4444' });
        }
    };

    const formatImageUrl = (path) => {
        if (!path) return "https://via.placeholder.com/150";
        if (path.startsWith("http")) return path.replace(/\\/g, "/");
        return `${API_BASE_URL}/${path.replace(/\\/g, "/")}`;
    };

    const getStockColor = (stock) => {
        if (stock >= 75) return "text-green-400";
        if (stock >= 25) return "text-yellow-400";
        return "text-red-400";
    };

    if (loading) return <div className="bg-black min-h-screen text-white flex items-center justify-center font-bold">Loading Inventory Management...</div>;

    return (
        <div className="bg-black min-h-screen text-white p-6">
            <Header />

            {/* Page Title & Global Actions */}
            <div className="mt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-white">Global Flavours</h1>
                    <p className="text-gray-500 text-sm mt-1">Manage the master list of flavors and liquid bases</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition flex items-center gap-4">
                    <FiPackage className="text-green-400 text-3xl" />
                    <div>
                        <p className="text-gray-400 text-xs">Total Flavours Tracked</p>
                        <h2 className="text-green-400 text-2xl font-bold">{summary.totalFlavors}</h2>
                    </div>
                </div>

                <div className="bg-[#1b1d1a] rounded-2xl p-6 border border-gray-700 shadow-lg hover:shadow-xl transition flex items-center gap-4">
                    <div className={`${summary.lowStockAlerts > 0 ? "text-red-500 bg-red-500/10" : "text-gray-500 bg-gray-500/10"} p-2 rounded-lg`}>
                        <FiPackage className="text-2xl" />
                    </div>
                    <div>
                        <p className="text-gray-400 text-xs">Low Stock Alerts (Global)</p>
                        <h2 className={`${summary.lowStockAlerts > 0 ? 'text-red-500' : 'text-white'} text-2xl font-bold`}>{summary.lowStockAlerts}</h2>
                    </div>
                </div>
            </div>

            <div className="mt-12 border-t border-gray-800 pt-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2">
                            Select Machine for Flavors
                        </h2>
                        <p className="text-gray-500 text-xs mt-1">Select a machine to manage its specific flavors</p>
                    </div>

                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <div className="relative w-full md:w-64" ref={dropdownRef}>
                            <div
                                className="bg-[#1b1d1a] border border-gray-700 text-white rounded-xl px-4 py-2 cursor-pointer flex justify-between items-center hover:border-green-500 transition"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            >
                                <span className="truncate pr-2 text-sm">
                                    {selectedDeviceId ? (() => {
                                        const dev = devices.find(d => String(d.device_id_PK) === String(selectedDeviceId));
                                        return dev ? `${dev.device_name}${dev.user_location ? ` (${dev.user_location})` : (dev.location ? ` (${dev.location})` : '')}` : "Select Machine";
                                    })() : "Select Machine"}
                                </span>
                                <FiChevronDown className={`transition-transform text-white/70 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                            </div>

                            {isDropdownOpen && (
                                <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-full min-w-[240px] bg-[#111211] border border-gray-800 rounded-xl shadow-2xl overflow-hidden">
                                    <div className="p-3 border-b border-gray-800">
                                        <div className="relative">
                                            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={12} />
                                            <input
                                                type="text"
                                                placeholder="Search Machine..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full bg-black border border-gray-800 text-gray-200 rounded-lg pl-8 pr-3 py-2 text-[11px] focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-gray-600"
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto custom-scrollbar text-[11px] font-medium p-1">
                                        {(() => {
                                            const filtered = devices.filter(d =>
                                                (d.device_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                (d.user_location || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                                                (d.location || "").toLowerCase().includes(searchQuery.toLowerCase())
                                            );
                                            if (filtered.length === 0) return <div className="px-3 py-4 text-gray-500 text-center font-normal">No machines found</div>;
                                            return filtered.map(device => (
                                                <div
                                                    key={device.device_id_PK}
                                                    className={`px-3 py-2 mx-1 mb-1 rounded-lg cursor-pointer transition-colors truncate ${String(selectedDeviceId) === String(device.device_id_PK) ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                    onClick={() => {
                                                        handleDeviceChange({ target: { value: device.device_id_PK } });
                                                        setIsDropdownOpen(false);
                                                        setSearchQuery("");
                                                    }}
                                                    title={`${device.device_name}${device.user_location ? ` (${device.user_location})` : (device.location ? ` (${device.location})` : '')}`}
                                                >
                                                    {device.device_name} {(device.user_location || device.location) && <span className="text-gray-400 text-xs">({device.user_location || device.location})</span>}
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="mt-6 border-t border-gray-800 pt-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            <FiPackage className="text-green-500" /> Global Flavour Catalog
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">Manage flavors registered for the selected machine</p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setIsNewFlavorModalOpen(true)}
                            className="bg-green-500 hover:bg-green-400 text-black px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition shadow-lg shadow-green-900/20 active:scale-95"
                        >
                            <FiPlus /> Add New Flavour
                        </button>
                        <button
                            onClick={() => setIsNewBaseModalOpen(true)}
                            className="bg-blue-500 hover:bg-blue-400 text-black px-6 py-2.5 rounded-xl font-bold flex items-center gap-2 transition shadow-lg shadow-blue-900/20 active:scale-95"
                        >
                            <FiPlus /> Add New Base
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {flavors.map((flavor) => (
                        <div key={flavor.flavors_id_PK} className="bg-[#1b1d1a] rounded-3xl overflow-hidden border border-gray-700 hover:border-green-500/50 transition-all duration-300 shadow-lg hover:shadow-2xl flex flex-col group">

                            {/* ── Image area with banner hover overlay ── */}
                            <div className="h-52 w-full bg-black relative overflow-hidden">
                                {/* Banner fades in on hover */}
                                {flavor.banner && (
                                    <img
                                        src={formatImageUrl(flavor.banner)}
                                        alt={`${flavor.name} banner`}
                                        className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-60 transition-opacity duration-500 select-none"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                )}
                                {/* Product image */}
                                <img
                                    src={formatImageUrl(flavor.image)}
                                    alt={flavor.name}
                                    className="relative h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105 select-none"
                                    onError={(e) => { e.target.src = "https://via.placeholder.com/300x200"; }}
                                />
                                {/* Action buttons */}
                                {(userRole === 'superadmin' || userRole === 'admin') && (
                                    <div className="absolute top-3 right-3 flex gap-2">
                                        <button
                                            onClick={() => {
                                                setEditFlavorTarget({ ...flavor });
                                                setIsEditFlavorModalOpen(true);
                                            }}
                                            className="bg-black/60 backdrop-blur-xl p-2 rounded-full border border-white/10 text-gray-400 hover:text-green-400 shadow-xl transition"
                                            title="Edit Flavor"
                                        >
                                            <FiEdit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteFlavor(flavor.flavors_id_PK)}
                                            className="bg-black/60 backdrop-blur-xl p-2 rounded-full border border-white/10 text-gray-400 hover:text-red-400 shadow-xl transition"
                                            title="Delete Flavor"
                                        >
                                            <FiTrash2 size={14} />
                                        </button>
                                    </div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#1b1d1a] to-transparent" />
                            </div>

                            {/* ── Card body ── */}
                            <div className="p-5 flex-1 flex flex-col gap-4">
                                {/* Name */}
                                <h3 className="text-lg font-bold text-white group-hover:text-green-400 transition-colors uppercase tracking-tight">
                                    {flavor.name}
                                </h3>

                                {/* Nutrition Facts */}
                                <div className="bg-black/40 rounded-xl p-3 border border-gray-800">
                                    <p className="text-[9px] text-gray-500 uppercase tracking-widest font-black mb-2">
                                        Nutrition Facts <span className="text-gray-700">(per serving)</span>
                                    </p>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[
                                            { label: 'Cal', value: flavor.Colories, unit: 'kcal', color: 'text-orange-400', bg: 'bg-orange-500/10' },
                                            { label: 'Protein', value: flavor.Protein, unit: 'g', color: 'text-blue-400', bg: 'bg-blue-500/10' },
                                            { label: 'Carbs', value: flavor.Carbs, unit: 'g', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
                                            { label: 'Fat', value: flavor.Fat, unit: 'g', color: 'text-red-400', bg: 'bg-red-500/10' },
                                            { label: 'Fibre', value: flavor.Fibre, unit: 'g', color: 'text-green-400', bg: 'bg-green-500/10' },
                                            { label: 'Sugar', value: flavor.Sugar, unit: 'g', color: 'text-pink-400', bg: 'bg-pink-500/10' },
                                        ].map(({ label, value, unit, color, bg }) => (
                                            <div key={label} className={`flex flex-col items-center ${bg} rounded-lg py-2 px-1 border border-gray-800/60`}>
                                                <span className={`text-sm font-black ${color}`}>{value ?? '–'}</span>
                                                {/* <span className="text-[12px] text-gray-600 uppercase font-semibold">{unit}</span> */}
                                                <span className="text-[12px] text-gray-500 uppercase tracking-tight mt-0.5">{label}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>



                                {/* Footer row */}
                                <div className="flex items-center justify-between mt-auto pt-1">
                                    <div className="bg-gray-900 px-3 py-1 rounded-lg border border-gray-800">
                                        <p className="text-[10px] text-gray-500 font-mono">ID: #00{flavor.flavors_id_PK}</p>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                                        <span className="text-[10px] text-green-500 font-bold uppercase">Active</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {flavors.length === 0 && (
                    <div className="py-20 text-center bg-[#1b1d1a] rounded-3xl border border-dashed border-gray-700">
                        <FiPackage className="mx-auto text-5xl text-gray-700 mb-4" />
                        <h3 className="text-xl font-bold text-gray-400">Your Catalog is Empty</h3>
                        <p className="text-gray-600 mt-2">Start by adding your first product using the button above.</p>
                    </div>
                )}

                <div className="mt-12 mb-8 border-t border-gray-800 pt-8">
                    <div>
                        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            <FiPackage className="text-blue-500" /> Global Base Catalog
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">Manage the master list of liquid bases</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {availableBases.map((base) => (
                        <div key={base.base_id_PK} className="bg-[#1b1d1a] rounded-3xl overflow-hidden border border-gray-700 hover:border-blue-500/50 transition-all duration-300 shadow-lg hover:shadow-2xl flex flex-col group">
                            {/* Image area */}
                            <div className="h-72 w-full bg-black relative overflow-hidden p-4">
                                <img
                                    src={formatImageUrl(base.image)}
                                    alt={base.name}
                                    className="relative h-full w-full object-contain opacity-90 transition-transform duration-500 group-hover:scale-110 select-none"
                                    onError={(e) => { e.target.src = "https://via.placeholder.com/300x200"; }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-[#1b1d1a] to-transparent pointer-events-none" />
                            </div>

                            {/* Card body */}
                            <div className="p-5 flex-1 flex flex-col gap-4">
                                <h3 className="text-lg font-bold text-white group-hover:text-blue-400 transition-colors uppercase tracking-tight">
                                    {base.name}
                                </h3>

                                <div className="bg-black/40 rounded-xl p-3 border border-gray-800">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-400 uppercase font-bold">Price</span>
                                        <span className="text-lg font-black text-blue-400">₹{base.price ?? '0'}</span>
                                    </div>
                                </div>

                                {/* Footer row */}
                                <div className="flex items-center justify-between mt-auto pt-1">
                                    <div className="bg-gray-900 px-3 py-1 rounded-lg border border-gray-800">
                                        <p className="text-[10px] text-gray-500 font-mono">ID: #00{base.base_id_PK}</p>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                                        <span className="text-[10px] text-blue-500 font-bold uppercase">Active</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {availableBases.length === 0 && (
                    <div className="py-20 text-center bg-[#1b1d1a] rounded-3xl border border-dashed border-gray-700 mt-8">
                        <FiPackage className="mx-auto text-5xl text-gray-700 mb-4" />
                        <h3 className="text-xl font-bold text-gray-400">Your Base Catalog is Empty</h3>
                        <p className="text-gray-600 mt-2">Start by adding your first base using the button above.</p>
                    </div>
                )}
            </div>

            {/* Add Flavor (Powder) Modal */}
            {isAddModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsAddModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-6">Assign New Flavour Stock</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Select Flavour</label>
                                <select
                                    value={newInventory.flavor_id}
                                    onChange={(e) => {
                                        const flavorId = e.target.value;
                                        const selectedFlavor = flavors.find(f => f.flavors_id_PK == flavorId);
                                        setNewInventory({
                                            ...newInventory,
                                            flavor_id: flavorId,
                                            price: selectedFlavor ? selectedFlavor.price : ""
                                        });
                                    }}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    <option value="">-- Select Flavor --</option>
                                    {flavors.map(f => (
                                        <option key={f.flavors_id_PK} value={f.flavors_id_PK}>{f.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Initial Stock Level (%)</label>
                                <input
                                    type="number"
                                    min="0" max="100"
                                    value={newInventory.stock}
                                    onChange={(e) => setNewInventory({ ...newInventory, stock: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Unit Price (₹)</label>
                                <input
                                    type="number"
                                    placeholder="99"
                                    value={newInventory.price}
                                    onChange={(e) => setNewInventory({ ...newInventory, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Assign Load Cell</label>
                                <select
                                    value={newInventory.loadCell_id_FK}
                                    onChange={(e) => setNewInventory({ ...newInventory, loadCell_id_FK: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                >
                                    <option value="">-- Select Load Cell --</option>
                                    {loadCells.map(lc => (
                                        <option key={lc.loadCell_id_PK} value={lc.loadCell_id_PK}>{lc.name}</option>
                                    ))}
                                </select>
                            </div>

                            <button
                                onClick={handleAddInventory}
                                className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-green-900/20"
                            >
                                Assign To Machine
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Base Modal */}
            {isAddBaseModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsAddBaseModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-blue-400 mb-6 font-primary">Add Liquid Base Stock</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Select Base</label>
                                <select
                                    value={newBaseInventory.base_id}
                                    onChange={(e) => {
                                        const baseId = e.target.value;
                                        const selectedBase = availableBases.find(b => b.base_id_PK == baseId);
                                        setNewBaseInventory({
                                            ...newBaseInventory,
                                            base_id: baseId,
                                            price: selectedBase ? selectedBase.price : ""
                                        });
                                    }}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">-- Select Base --</option>
                                    {availableBases.map(b => (
                                        <option key={b.base_id_PK} value={b.base_id_PK}>{b.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Stock Level (%)</label>
                                <input
                                    type="number"
                                    min="0" max="100"
                                    value={newBaseInventory.stock}
                                    onChange={(e) => setNewBaseInventory({ ...newBaseInventory, stock: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Price (₹)</label>
                                <input
                                    type="number"
                                    value={newBaseInventory.price}
                                    onChange={(e) => setNewBaseInventory({ ...newBaseInventory, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <button
                                onClick={handleAddBaseInventory}
                                className="w-full py-3 bg-blue-500 hover:bg-blue-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-blue-900/20"
                            >
                                Add Base To Machine
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Base Stock Modal */}
            {isEditBaseModalOpen && editBaseTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsEditBaseModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-blue-400 mb-2">Update Base Level</h2>
                        <p className="text-gray-400 text-xs mb-6 uppercase tracking-tight">Refilling {editBaseTarget.base_name}</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2 text-center text-blue-400/60">New Level: {editBaseTarget.stock}%</label>
                                <input
                                    type="range"
                                    min="0" max="100"
                                    value={editBaseTarget.stock}
                                    onChange={(e) => setEditBaseTarget({ ...editBaseTarget, stock: e.target.value })}
                                    className="w-full accent-blue-500 h-1.5 bg-black rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-gray-600 mt-2 font-bold uppercase">
                                    <span>Empty</span>
                                    <span>Half</span>
                                    <span>Full</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-2">
                                {[20, 50, 80, 100].map(val => (
                                    <button
                                        key={val}
                                        onClick={() => setEditBaseTarget({ ...editBaseTarget, stock: val })}
                                        className="py-1.5 bg-gray-900 border border-gray-700 rounded-lg text-[10px] font-bold hover:bg-blue-500 hover:text-black transition"
                                    >
                                        {val}%
                                    </button>
                                ))}
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Price (₹)</label>
                                <input
                                    type="number"
                                    value={editBaseTarget.price}
                                    onChange={(e) => setEditBaseTarget({ ...editBaseTarget, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            {/* <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Assign Load Cell</label>
                                <select
                                    value={editBaseTarget.loadCell_id_FK || ""}
                                    onChange={(e) => setEditBaseTarget({ ...editBaseTarget, loadCell_id_FK: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                >
                                    <option value="">-- Select Load Cell --</option>
                                    {loadCells.map(lc => (
                                        <option key={lc.loadCell_id_PK} value={lc.loadCell_id_PK}>{lc.name}</option>
                                    ))}
                                </select>
                            </div> */}

                            <button
                                onClick={handleEditBaseStock}
                                className="w-full py-3 bg-blue-500 hover:bg-blue-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-blue-900/20"
                            >
                                Confirm Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Stock Modal */}
            {isEditModalOpen && editTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
                        <button
                            onClick={() => setIsEditModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-2">Update Stock Level</h2>
                        <p className="text-gray-400 text-xs mb-6 uppercase">Refilling {editTarget.flavor_name}</p>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2 text-center">New Percentage: {editTarget.stock}%</label>
                                <input
                                    type="range"
                                    min="0" max="100"
                                    value={editTarget.stock}
                                    onChange={(e) => setEditTarget({ ...editTarget, stock: e.target.value })}
                                    className="w-full accent-green-500 h-2 bg-black rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-[10px] text-gray-600 mt-2">
                                    <span>0% (Empty)</span>
                                    <span>50%</span>
                                    <span>100% (Full)</span>
                                </div>
                            </div>

                            <div ref={editFlavorDropdownRef} className="relative">
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Select Flavour</label>
                                <div
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 cursor-pointer flex justify-between items-center transition hover:border-green-500/50"
                                    onClick={() => setIsEditFlavorDropdownOpen(!isEditFlavorDropdownOpen)}
                                >
                                    <span className="truncate text-white text-sm">
                                        {editTarget.flavor_id_FK
                                            ? (flavors.find(f => String(f.flavors_id_PK) === String(editTarget.flavor_id_FK))?.name || editTarget.flavor_name || "-- Select Flavor --")
                                            : "-- Select Flavor --"}
                                    </span>
                                    <FiChevronDown size={14} className={`transition-transform text-gray-500 ${isEditFlavorDropdownOpen ? 'rotate-180' : ''}`} />
                                </div>

                                {isEditFlavorDropdownOpen && (
                                    <div className="absolute top-[calc(100%+8px)] left-0 z-50 w-full bg-[#111211] border border-gray-800 rounded-xl shadow-2xl overflow-hidden">
                                        <div className="p-3 border-b border-gray-800">
                                            <div className="relative">
                                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={14} />
                                                <input
                                                    type="text"
                                                    placeholder="Search flavor..."
                                                    value={editFlavorSearchQuery}
                                                    onChange={(e) => setEditFlavorSearchQuery(e.target.value)}
                                                    className="w-full bg-black border border-gray-800 text-gray-200 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-green-500/50 transition-colors placeholder:text-gray-600"
                                                    autoFocus
                                                />
                                            </div>
                                        </div>
                                        <div className="max-h-64 overflow-y-auto custom-scrollbar p-1">
                                            <div
                                                className={`px-3 py-2.5 mx-1 mb-1 rounded-lg cursor-pointer transition-colors text-sm truncate ${!editTarget.flavor_id_FK ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                onClick={() => {
                                                    setEditTarget({ ...editTarget, flavor_id_FK: "" });
                                                    setIsEditFlavorDropdownOpen(false);
                                                    setEditFlavorSearchQuery("");
                                                }}
                                            >
                                                -- Select Flavor --
                                            </div>
                                            {(() => {
                                                const filtered = flavors.filter(f => (f.name || "").toLowerCase().includes(editFlavorSearchQuery.toLowerCase()));
                                                if (filtered.length === 0) return <div className="px-3 py-4 text-gray-500 text-center text-sm">No flavors found</div>;
                                                return filtered.map(f => (
                                                    <div
                                                        key={f.flavors_id_PK}
                                                        className={`px-3 py-2.5 mx-1 mb-1 rounded-lg cursor-pointer transition-colors text-sm truncate ${String(editTarget.flavor_id_FK) === String(f.flavors_id_PK) ? 'bg-green-500/10 text-green-400 font-bold' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                                                        onClick={() => {
                                                            setEditTarget({
                                                                ...editTarget,
                                                                flavor_id_FK: f.flavors_id_PK,
                                                                flavor_name: f.name,
                                                                price: f.price || editTarget.price
                                                            });
                                                            setIsEditFlavorDropdownOpen(false);
                                                            setEditFlavorSearchQuery("");
                                                        }}
                                                    >
                                                        {f.name}
                                                    </div>
                                                ));
                                            })()}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Unit Price (₹)</label>
                                <input
                                    type="number"
                                    value={editTarget.price}
                                    onChange={(e) => setEditTarget({ ...editTarget, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Assign Load Cell</label>
                                <select
                                    value={editTarget.loadCell_id_FK || ""}
                                    onChange={(e) => setEditTarget({ ...editTarget, loadCell_id_FK: e.target.value })}
                                    disabled
                                    className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-gray-500 cursor-not-allowed outline-none"
                                >
                                    <option value="">-- Select Load Cell --</option>
                                    {loadCells.map(lc => (
                                        <option key={lc.loadCell_id_PK} value={lc.loadCell_id_PK}>{lc.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-4 gap-2">
                                {[25, 50, 75, 100].map(val => (
                                    <button
                                        key={val}
                                        onClick={() => setEditTarget({ ...editTarget, stock: val })}
                                        className="py-2 bg-gray-900 border border-gray-700 rounded-lg text-xs font-bold hover:bg-green-500 hover:text-black transition"
                                    >
                                        {val}%
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={handleEditStock}
                                className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-4 transition shadow-lg shadow-green-900/20"
                            >
                                Confirm Refill
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* New Flavor Modal (Master List) */}
            {isNewFlavorModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-2xl rounded-2xl p-6 shadow-2xl relative my-8">
                        <button
                            onClick={() => setIsNewFlavorModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-6 font-primary">Add Global Flavour</h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Flavour Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Chocolate"
                                    value={newFlavor.name}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, name: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            {userRole === 'admin' && (
                                <div className="md:col-span-2">
                                    <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Select Machine</label>
                                    <select
                                        value={newFlavor.device_id}
                                        onChange={(e) => setNewFlavor({ ...newFlavor, device_id: e.target.value })}
                                        className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                    >
                                        <option value="">-- Select Machine --</option>
                                        {devices.map(d => (
                                            <option key={d.device_id_PK} value={d.device_id_PK}>
                                                {d.device_name}{d.user_location ? ` (${d.user_location})` : (d.location ? ` (${d.location})` : '')}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Calories</label>
                                <input
                                    type="number"
                                    placeholder="120"
                                    value={newFlavor.Colories}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, Colories: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Protein (g)</label>
                                <input
                                    type="number"
                                    placeholder="5"
                                    value={newFlavor.Protein}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, Protein: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Carbs (g)</label>
                                <input
                                    type="number"
                                    placeholder="20"
                                    value={newFlavor.Carbs}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, Carbs: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Fat (g)</label>
                                <input
                                    type="number"
                                    placeholder="3"
                                    value={newFlavor.Fat}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, Fat: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Fibre (g)</label>
                                <input
                                    type="number"
                                    placeholder="2"
                                    value={newFlavor.Fibre}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, Fibre: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Sugar (g)</label>
                                <input
                                    type="number"
                                    placeholder="4"
                                    value={newFlavor.Sugar}
                                    onChange={(e) => setNewFlavor({ ...newFlavor, Sugar: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Flavour Image</label>
                                    <input
                                        type="file"
                                        onChange={(e) => setNewFlavor({ ...newFlavor, image: e.target.files[0] })}
                                        className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Banner Image</label>
                                    <input
                                        type="file"
                                        onChange={(e) => setNewFlavor({ ...newFlavor, banner: e.target.files[0] })}
                                        className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20"
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleAddNewFlavor}
                            className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-8 transition shadow-lg shadow-green-900/20"
                        >
                            Register Flavour
                        </button>
                    </div>
                </div>
            )}

            {/* New Base Modal (Master List) */}
            {isNewBaseModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-md rounded-2xl p-6 shadow-2xl relative my-8">
                        <button
                            onClick={() => setIsNewBaseModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-blue-400 mb-6 font-primary">Add Global Base</h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Base Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Milk"
                                    value={newBase.name}
                                    onChange={(e) => setNewBase({ ...newBase, name: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Price (₹)</label>
                                <input
                                    type="number"
                                    placeholder="20"
                                    value={newBase.price}
                                    onChange={(e) => setNewBase({ ...newBase, price: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Base Image</label>
                                <input
                                    type="file"
                                    onChange={(e) => setNewBase({ ...newBase, image: e.target.files[0] })}
                                    className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-blue-500/10 file:text-blue-400 hover:file:bg-blue-500/20"
                                />
                            </div>
                        </div>

                        <button
                            onClick={handleAddNewBase}
                            className="w-full py-3 bg-blue-500 hover:bg-blue-400 text-black font-bold rounded-xl mt-8 transition shadow-lg shadow-blue-900/20"
                        >
                            Register Base
                        </button>
                    </div>
                </div>
            )}

            {/* Edit Global Flavor Modal */}
            {isEditFlavorModalOpen && editFlavorTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-[#1b1d1a] border border-gray-700 w-full max-w-2xl rounded-2xl p-6 shadow-2xl relative my-8">
                        <button
                            onClick={() => setIsEditFlavorModalOpen(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-white transition"
                        >
                            <FiX size={24} />
                        </button>

                        <h2 className="text-xl font-bold text-green-400 mb-1 uppercase tracking-tight">Edit Flavour Details</h2>
                        <p className="text-gray-500 text-xs mb-6 uppercase tracking-widest">Editing: {editFlavorTarget.name}</p>

                        {/* Current image previews */}
                        <div className="flex gap-4 mb-6">
                            {editFlavorTarget.image && (
                                <div className="flex-1">
                                    <p className="text-[9px] text-gray-500 uppercase font-bold mb-1">Current Image</p>
                                    <img
                                        src={formatImageUrl(editFlavorTarget.image)}
                                        alt="current"
                                        className="h-24 w-full object-cover rounded-xl border border-gray-700 opacity-80"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}
                            {editFlavorTarget.banner && (
                                <div className="flex-1">
                                    <p className="text-[9px] text-gray-500 uppercase font-bold mb-1">Current Banner</p>
                                    <img
                                        src={formatImageUrl(editFlavorTarget.banner)}
                                        alt="current banner"
                                        className="h-24 w-full object-cover rounded-xl border border-gray-700 opacity-80"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Name – full width */}
                            <div className="md:col-span-2">
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Flavour Name</label>
                                <input
                                    type="text"
                                    value={editFlavorTarget.name}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, name: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            {/* Nutritional fields */}
                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Calories</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.Colories ?? ''}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, Colories: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Protein (g)</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.Protein ?? ''}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, Protein: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Carbs (g)</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.Carbs ?? ''}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, Carbs: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Fat (g)</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.Fat ?? ''}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, Fat: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Fibre (g)</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.Fibre ?? ''}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, Fibre: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Sugar (g)</label>
                                <input
                                    type="number"
                                    value={editFlavorTarget.Sugar ?? ''}
                                    onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, Sugar: e.target.value })}
                                    className="w-full bg-black border border-gray-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-green-500 outline-none"
                                />
                            </div>

                            {/* Image uploads – full width row */}
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Replace Image (Optional)</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, imageFile: e.target.files[0] })}
                                        className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs text-gray-400 uppercase font-bold mb-2">Replace Banner (Optional)</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => setEditFlavorTarget({ ...editFlavorTarget, bannerFile: e.target.files[0] })}
                                        className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-green-500/10 file:text-green-400 hover:file:bg-green-500/20"
                                    />
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handleEditFlavor}
                            className="w-full py-3 bg-green-500 hover:bg-green-400 text-black font-bold rounded-xl mt-8 transition shadow-lg shadow-green-900/20 active:scale-95"
                        >
                            Save Changes
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FlavorsPage;
